import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import mermaid from 'mermaid';
import '../styles/landing.css';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3393/api';

interface SharedDiagram {
  id: string;
  title: string;
  type: 'mermaid' | 'excalidraw';
  mermaidCode: string;
  excalidrawData?: Record<string, unknown>;
  theme: string;
  shareMode: 'view' | 'edit';
  editingBy?: string | null;
}

function getEditorId() {
  let id = sessionStorage.getItem('sf.editorId');
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem('sf.editorId', id);
  }
  return id;
}

function LogoIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M3 4l9 16 9-16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M7 4h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
    </svg>
  );
}

export function SharedDiagramPage() {
  const { token } = useParams<{ token: string }>();
  const [diagram, setDiagram] = useState<SharedDiagram | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [svgHtml, setSvgHtml] = useState('');
  const [editCode, setEditCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [lockedBy, setLockedBy] = useState<string | null>(null);
  const [hasLock, setHasLock] = useState(false);
  const renderCounter = useRef(0);
  const editorId = useRef(getEditorId());

  // Fetch shared diagram
  useEffect(() => {
    if (!token) return;
    fetch(`${BASE_URL}/shared/${token}`)
      .then(async (res) => {
        if (!res.ok) throw new Error('Diagram not found');
        const body = await res.json();
        return body.data || body;
      })
      .then((d) => {
        setDiagram(d);
        setEditCode(d.mermaidCode || '');
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  // Acquire edit lock + heartbeat
  useEffect(() => {
    if (!token || !diagram || diagram.shareMode !== 'edit') return;

    if (diagram.editingBy && diagram.editingBy !== editorId.current) {
      setLockedBy(diagram.editingBy);
      return;
    }

    const tryLock = async () => {
      try {
        const res = await fetch(`${BASE_URL}/shared/${token}/lock`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ editorId: editorId.current }),
        });
        const body = await res.json();
        const data = body.data || body;
        if (data.locked) {
          setLockedBy(data.editingBy);
          setHasLock(false);
        } else {
          setLockedBy(null);
          setHasLock(true);
        }
      } catch { /* ignore */ }
    };

    void tryLock();
    const interval = setInterval(tryLock, 60_000); // heartbeat every 60s

    return () => {
      clearInterval(interval);
      // Release lock on unmount
      void fetch(`${BASE_URL}/shared/${token}/lock`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ editorId: editorId.current }),
      }).catch(() => {});
    };
  }, [token, diagram]);

  // Render Mermaid diagram
  useEffect(() => {
    if (!diagram || diagram.type !== 'mermaid') return;
    const code = editCode || diagram.mermaidCode;
    if (!code?.trim()) return;

    mermaid.initialize({
      startOnLoad: false,
      theme: diagram.theme === 'dark' ? 'dark' : 'default',
      securityLevel: 'loose',
    });

    renderCounter.current += 1;
    const id = `shared-mermaid-${renderCounter.current}`;
    mermaid.render(id, code)
      .then(({ svg }) => setSvgHtml(svg))
      .catch(() => setSvgHtml(''));
  }, [diagram, editCode]);

  // Save edits
  const handleSave = useCallback(async () => {
    if (!token || !editCode) return;
    setSaving(true);
    try {
      const res = await fetch(`${BASE_URL}/shared/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mermaidCode: editCode }),
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  }, [token, editCode]);

  if (loading) {
    return (
      <div className="landing" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="ds-auth-spinner" />
      </div>
    );
  }

  if (error || !diagram) {
    return (
      <div className="landing" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', textAlign: 'center', padding: '40px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '16px' }}>Diagram not found</h1>
        <p style={{ fontSize: '16px', color: '#94a3b8', marginBottom: '32px' }}>This shared link may have expired or been disabled.</p>
        <Link to="/" className="landing-btn landing-btn--primary">Go to Sketchflow</Link>
      </div>
    );
  }

  const isEditable = diagram.shareMode === 'edit' && diagram.type === 'mermaid';

  return (
    <div className="landing" style={{ minHeight: '100vh' }}>
      <nav className="landing-nav">
        <Link to="/" className="landing-nav__brand" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <LogoIcon />
          <span className="landing-nav__name">Sketchflow</span>
        </Link>
        <div className="landing-nav__actions">
          <Link to="/register" className="landing-btn landing-btn--primary landing-btn--sm">Create your own</Link>
        </div>
      </nav>

      <div className="shared-diagram-container">
        <div className="shared-diagram-header">
          <h1 className="shared-diagram-title">{diagram.title}</h1>
          <p className="shared-diagram-meta">
            Shared diagram &middot; {diagram.type === 'mermaid' ? 'Mermaid' : 'Excalidraw'}
            {isEditable && <span className="shared-diagram-badge">Editable</span>}
          </p>
        </div>

        {/* Rendered Mermaid SVG */}
        {diagram.type === 'mermaid' && svgHtml && (
          <div
            className="shared-diagram-canvas"
            dangerouslySetInnerHTML={{ __html: svgHtml }}
          />
        )}

        {/* Fallback: raw code if rendering fails */}
        {diagram.type === 'mermaid' && !svgHtml && diagram.mermaidCode && (
          <div className="shared-diagram-code">
            <pre>{diagram.mermaidCode}</pre>
          </div>
        )}

        {/* Excalidraw placeholder */}
        {diagram.type === 'excalidraw' && (
          <div className="shared-diagram-code" style={{ textAlign: 'center', color: '#94a3b8', padding: '64px 32px' }}>
            Excalidraw diagram preview is not yet available in shared view.
            <br />
            <Link to="/register" style={{ color: 'var(--ds-brand-violet)', marginTop: '12px', display: 'inline-block' }}>
              Open in editor to view
            </Link>
          </div>
        )}

        {/* Lock banner */}
        {isEditable && lockedBy && !hasLock && (
          <div className="shared-diagram-lock-banner">
            Someone else is currently editing this diagram. You can view it but edits are disabled until they leave.
          </div>
        )}

        {/* Editable code editor */}
        {isEditable && (
          <div className="shared-diagram-editor">
            <div className="shared-diagram-editor__head">
              <span>Mermaid Code {hasLock && <span style={{ color: 'var(--ds-brand-violet)', fontSize: '12px', marginLeft: '8px' }}>Editing</span>}</span>
              <button
                type="button"
                className="landing-btn landing-btn--primary landing-btn--sm"
                onClick={handleSave}
                disabled={saving || !hasLock}
              >
                {saving ? 'Saving...' : saved ? 'Saved!' : 'Save changes'}
              </button>
            </div>
            <textarea
              className="shared-diagram-editor__textarea"
              value={editCode}
              onChange={(e) => setEditCode(e.target.value)}
              spellCheck={false}
              disabled={!hasLock}
              style={!hasLock ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            />
          </div>
        )}

        <div style={{ marginTop: '32px', textAlign: 'center', paddingBottom: '40px' }}>
          <Link to="/register" className="landing-btn landing-btn--primary landing-btn--lg">
            Start creating diagrams for free
          </Link>
        </div>
      </div>
    </div>
  );
}
